import { formatArticleDate } from '@archetelos/lib/date';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { subStatusView } from './subscriptionView';

import { FounderChip } from '@/features/subscriber/FounderChip';
import { useAuth } from '@/lib/auth/useAuth';
import { computeTierDisplay } from '@/lib/pricing';
import { subscriptionQueries } from '@/lib/queries/subscription';
import { StripeFunctionError, startBillingPortal } from '@/lib/stripe-client';

export function SubscriptionTab() {
  const { user } = useAuth();
  const subscription = useQuery(subscriptionQueries.current(user?.id));
  const [opening, setOpening] = useState(false);

  const openPortal = async () => {
    setOpening(true);
    try {
      const url = await startBillingPortal();
      window.location.href = url;
    } catch (err) {
      if (err instanceof StripeFunctionError && err.code === 'no_stripe_customer') {
        toast.error("You don't have a billing record yet. Subscribe first.");
      } else {
        toast.error(err instanceof Error ? err.message : 'Unable to open billing portal');
      }
    } finally {
      setOpening(false);
    }
  };

  if (subscription.isLoading) {
    return <p className="text-sm text-ink/60">Loading subscription state…</p>;
  }

  const sub = subscription.data;

  // No subscription record at all → prompt to subscribe.
  if (!sub) {
    return (
      <div className="rounded-md border border-ink/10 bg-parchment p-6 text-center">
        <p className="font-serif text-lg text-ink">No subscription on file</p>
        <p className="mt-2 font-sans text-sm text-ink/70 pretty">
          A subscription supports the editorial work and unlocks the archive.
        </p>
        <Link
          to="/subscribe"
          className="mt-6 inline-block rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
        >
          Become a member
        </Link>
      </div>
    );
  }

  const isCanceled = sub.status === 'canceled' || sub.status === 'incomplete';
  const { label: statusLabel, pastDue: isPastDue } = subStatusView(sub.status);

  if (isCanceled) {
    return (
      <div className="rounded-md border border-ink/10 bg-parchment p-6">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Status</p>
        <p className="mt-2 font-serif text-lg text-ink">Canceled</p>
        <p className="mt-3 font-sans text-sm text-ink/70 pretty">
          You can resubscribe at any time.
        </p>
        {sub.is_founder && (
          <p className="mt-2 font-sans text-xs italic text-ink/50">
            Note: resubscribing after cancellation does not restore founding-member status.
          </p>
        )}
        <Link
          to="/subscribe"
          className="mt-6 inline-block rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
        >
          Resubscribe
        </Link>
      </div>
    );
  }

  const display = computeTierDisplay({
    tier: sub.tier as 'monthly' | 'annual',
    isFounder: sub.is_founder,
  });
  const nextDate = sub.current_period_end ? new Date(sub.current_period_end) : null;
  const ongoingAmount = display.ongoingDisplay;
  const firstCycleAmount = display.firstCycleDisplay;

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-ink/10 bg-parchment p-6">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Status</p>
        <p className="mt-2 font-serif text-lg text-ink">
          {statusLabel}
          {sub.cancel_at_period_end && !isPastDue && (
            <span className="ml-2 font-sans text-sm font-normal text-ink/60">
              · cancels at period end
            </span>
          )}
        </p>
        {isPastDue && (
          <p className="mt-2 font-sans text-sm text-signal">
            Your last payment didn&apos;t go through — update your card in the billing portal.
          </p>
        )}

        {sub.is_founder && sub.founder_number !== null && (
          <div className="mt-3">
            <FounderChip number={sub.founder_number} />
          </div>
        )}

        <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-sans text-xs uppercase tracking-[0.12em] text-ink/50">Tier</dt>
            <dd className="mt-1 font-serif text-base text-ink">
              {sub.tier === 'monthly' ? 'Monthly' : 'Annual'}
            </dd>
          </div>
          <div>
            <dt className="font-sans text-xs uppercase tracking-[0.12em] text-ink/50">
              Next charge
            </dt>
            <dd className="mt-1 font-serif text-base text-ink">
              {ongoingAmount}
              {firstCycleAmount !== ongoingAmount && (
                <span className="ml-2 font-sans text-xs text-ink/50">
                  (first cycle was {firstCycleAmount})
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="font-sans text-xs uppercase tracking-[0.12em] text-ink/50">
              Next billing date
            </dt>
            <dd className="mt-1 font-serif text-base text-ink">
              {nextDate ? formatArticleDate(nextDate) : '—'}
            </dd>
          </div>
        </dl>

        <button
          type="button"
          onClick={() => void openPortal()}
          disabled={opening}
          className="mt-8 rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
        >
          {opening ? 'Opening…' : 'Manage subscription'}
        </button>
        <p className="mt-3 text-xs text-ink/50">
          Update your card, change tier, or cancel — all from Stripe&apos;s billing portal.
        </p>
      </div>
    </div>
  );
}
